import React, { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, increment } from 'firebase/firestore';
import { NoticeModel, NOTICE_CATEGORIES, CompanyName } from '../../../models';
import { Megaphone, Search, Pin, Calendar, Eye, ChevronRight, X, BookOpen, Sparkles, Filter } from 'lucide-react';

const CATEGORIES = ['전체', ...NOTICE_CATEGORIES];

const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
    '학원공지': { bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-indigo-500/20' },
    '국어과': { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/20' },
    '수학과': { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
    '과학과': { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
    '영어과': { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
};

const IntroNotice: React.FC = () => {
    const [notices, setNotices] = useState<NoticeModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('전체');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedNotice, setSelectedNotice] = useState<NoticeModel | null>(null);

    useEffect(() => {
        const q = query(collection(db, 'notices'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list: NoticeModel[] = [];
            snapshot.forEach((docSnap) => {
                list.push({ id: docSnap.id, ...docSnap.data() } as NoticeModel);
            });
            setNotices(list);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching notices:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleOpenNotice = async (notice: NoticeModel) => {
        setSelectedNotice(notice);
        // Increment view count in firestore
        try {
            await updateDoc(doc(db, 'notices', notice.id), {
                viewCount: increment(1)
            });
        } catch (e) {
            console.error('Error incrementing view count:', e);
        }
    };

    // Filter notices
    const filteredNotices = notices.filter((notice) => {
        const matchesCategory = activeCategory === '전체' || notice.category === activeCategory;
        const matchesSearch = searchQuery === '' ||
            notice.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            notice.content.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    // Pinned notices first
    const sortedNotices = [...filteredNotices].sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return (b.createdAt || 0) - (a.createdAt || 0);
    });

    const formatDate = (timestamp?: number) => {
        if (!timestamp) return '';
        const d = new Date(timestamp);
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    };

    const stripHtml = (html: string) => {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    return (
        <div className="animate-in fade-in duration-700">
            {/* Hero Section */}
            <section className="py-16 md:py-20 px-4 md:px-8 bg-gradient-to-br from-slate-900 via-[#0D1630] to-indigo-950 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(94,23,235,0.15)_0%,transparent_70%)] pointer-events-none" />
                <div className="max-w-5xl mx-auto text-center space-y-6 relative z-10">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 text-indigo-300 font-black text-[10px] uppercase tracking-wider border border-indigo-500/20">
                        <Megaphone size={14} className="text-indigo-400" /> Notice & Announcement
                    </div>
                    <h1 className="text-2xl sm:text-4xl md:text-5xl font-black leading-tight">
                        {CompanyName} <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-300 to-violet-400">
                            공지사항 & 과목별 소식
                        </span>
                    </h1>
                    <p className="text-slate-400 font-medium text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
                        학원의 주요 일정 및 과목별(국어, 수학, 과학, 영어) 학습 공지사항을 확인하세요.
                    </p>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-12 md:py-16 px-4 md:px-8 max-w-6xl mx-auto">
                {/* Search & Category Header */}
                <div className="space-y-6 mb-10">
                    {/* Category Filter Tabs */}
                    <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
                        {CATEGORIES.map((cat) => {
                            const isActive = activeCategory === cat;
                            return (
                                <button
                                    key={cat}
                                    onClick={() => setActiveCategory(cat)}
                                    className={`px-5 py-2.5 rounded-2xl text-xs sm:text-sm font-black transition-all whitespace-nowrap border ${
                                        isActive
                                            ? 'bg-indigo-600 text-white border-indigo-500 shadow-lg shadow-indigo-600/25 scale-105'
                                            : 'bg-slate-900/60 text-slate-400 border-slate-800 hover:text-white hover:border-slate-700'
                                    }`}
                                >
                                    {cat}
                                </button>
                            );
                        })}
                    </div>

                    {/* Search Bar */}
                    <div className="relative max-w-md">
                        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                            type="text"
                            placeholder="공지사항 제목 또는 내용 검색..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-slate-900/60 border border-slate-800 rounded-2xl text-sm font-bold text-slate-200 placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition-all"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Notice List */}
                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-24 rounded-2xl bg-slate-900/40 border border-slate-800/60 animate-pulse" />
                        ))}
                    </div>
                ) : sortedNotices.length === 0 ? (
                    <div className="text-center py-24 bg-slate-900/40 rounded-3xl border border-slate-800/60 space-y-3">
                        <Megaphone className="mx-auto text-slate-600 mb-2" size={48} />
                        <p className="text-slate-400 font-bold text-base">등록된 공지사항이 없습니다.</p>
                        <p className="text-slate-500 text-xs font-medium">검색어나 카테고리 필터를 변경해 보세요.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {sortedNotices.map((notice) => {
                            const badgeStyle = CATEGORY_COLORS[notice.category || '학원공지'] || {
                                bg: 'bg-indigo-500/10',
                                text: 'text-indigo-400',
                                border: 'border-indigo-500/20'
                            };

                            return (
                                <div
                                    key={notice.id}
                                    onClick={() => handleOpenNotice(notice)}
                                    className={`group cursor-pointer p-5 sm:p-6 rounded-2xl bg-slate-900/60 border transition-all duration-300 hover:scale-[1.005] hover:shadow-xl hover:shadow-indigo-950/20 ${
                                        notice.isPinned
                                            ? 'border-indigo-500/40 bg-gradient-to-r from-indigo-950/30 via-slate-900/60 to-slate-900/60'
                                            : 'border-slate-800/80 hover:border-slate-700'
                                    }`}
                                >
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div className="space-y-2 flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {notice.isPinned && (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/30 font-black text-[10px]">
                                                        <Pin size={11} className="rotate-45" /> 필독 공지
                                                    </span>
                                                )}
                                                <span className={`px-2.5 py-1 rounded-lg font-black text-[10px] border ${badgeStyle.bg} ${badgeStyle.text} ${badgeStyle.border}`}>
                                                    {notice.category || '학원공지'}
                                                </span>
                                            </div>
                                            <h3 className="text-base sm:text-lg font-black text-slate-100 group-hover:text-indigo-300 transition-colors line-clamp-1">
                                                {notice.title}
                                            </h3>
                                            <p className="text-xs sm:text-sm text-slate-400 line-clamp-2 leading-relaxed font-medium">
                                                {stripHtml(notice.content)}
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-4 text-slate-500 text-xs font-bold shrink-0 self-end md:self-center pt-2 md:pt-0">
                                            <span className="flex items-center gap-1.5">
                                                <Calendar size={13} className="text-slate-600" />
                                                {formatDate(notice.createdAt)}
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                <Eye size={13} className="text-slate-600" />
                                                {notice.viewCount || 0}
                                            </span>
                                            <ChevronRight size={18} className="text-slate-600 group-hover:text-indigo-400 group-hover:translate-x-1 transition-all" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* Notice Detail Modal */}
            {selectedNotice && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300"
                        onClick={() => setSelectedNotice(null)}
                    />
                    <div className="relative w-full max-w-3xl bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-300 z-10 text-slate-100">
                        {/* Header */}
                        <div className="p-6 sm:p-8 border-b border-slate-800 bg-slate-900/80 sticky top-0 z-10 flex items-start justify-between gap-4">
                            <div className="space-y-3 flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    {selectedNotice.isPinned && (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/30 font-black text-[10px]">
                                            <Pin size={11} className="rotate-45" /> 필독 공지
                                        </span>
                                    )}
                                    <span className={`px-2.5 py-1 rounded-lg font-black text-[10px] border ${(CATEGORY_COLORS[selectedNotice.category || '학원공지'] || CATEGORY_COLORS['학원공지']).bg} ${(CATEGORY_COLORS[selectedNotice.category || '학원공지'] || CATEGORY_COLORS['학원공지']).text} ${(CATEGORY_COLORS[selectedNotice.category || '학원공지'] || CATEGORY_COLORS['학원공지']).border}`}>
                                        {selectedNotice.category || '학원공지'}
                                    </span>
                                </div>
                                <h2 className="text-xl sm:text-2xl font-black text-white leading-snug">
                                    {selectedNotice.title}
                                </h2>
                                <div className="flex items-center gap-4 text-xs font-bold text-slate-400 pt-1">
                                    <span className="flex items-center gap-1.5">
                                        <Calendar size={13} /> {formatDate(selectedNotice.createdAt)}
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <Eye size={13} /> {selectedNotice.viewCount || 0}회 읽음
                                    </span>
                                    {selectedNotice.authorName && (
                                        <span className="text-slate-500">작성자: {selectedNotice.authorName}</span>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedNotice(null)}
                                className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors shrink-0"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 sm:p-8 overflow-y-auto flex-1 custom-scrollbar space-y-6">
                            <div
                                className="prose prose-invert max-w-none text-slate-300 text-sm sm:text-base leading-relaxed font-medium"
                                dangerouslySetInnerHTML={{ __html: selectedNotice.content }}
                            />
                        </div>

                        {/* Footer */}
                        <div className="p-4 sm:p-6 border-t border-slate-800 bg-slate-900/60 flex justify-end">
                            <button
                                onClick={() => setSelectedNotice(null)}
                                className="px-6 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs sm:text-sm transition-all"
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IntroNotice;
